// Operator engine API (super-admin only). Thin promises in the pocAction
// style, plus the SSE reader for a turn (fetch + reader, like the Insight
// Engine stream, because it is a POST with a bearer token).
import axios from 'axios';
import config from '../../config';
import { getUserToken } from '../../utils/helpers';

const authHeaders = () => ({ headers: { Authorization: 'Bearer ' + getUserToken() } });
const base = () => `${config.api.base_url}/operator`;
const unwrap = (r) => r.data?.data;

export const listOperatorChatsApi = () => axios.get(`${base()}/chats`, authHeaders()).then(unwrap);
export const createOperatorChatApi = (title) => axios.post(`${base()}/chats`, title ? { title } : {}, authHeaders()).then(unwrap);
export const getOperatorChatApi = (chatId) => axios.get(`${base()}/chats/${chatId}`, authHeaders()).then(unwrap);
export const deleteOperatorChatApi = (chatId) => axios.delete(`${base()}/chats/${chatId}`, authHeaders()).then(unwrap);
export const abortOperatorTurnApi = (chatId) => axios.post(`${base()}/chats/${chatId}/abort`, {}, authHeaders()).then(unwrap);
// A message sent while Copilot works: queued for its next step ({queued, message}).
export const queueOperatorMessageApi = (chatId, content, context) => axios.post(`${base()}/chats/${chatId}/queue`, { content, ...(context ? { context } : {}) }, authHeaders()).then(unwrap);
export const discardQueuedApi = (chatId, ids) => axios.post(`${base()}/chats/${chatId}/queue/discard`, { ids }, authHeaders()).then(unwrap);
// @ references for the side panel: the person's studies, libraries, agents, surveys.
export const copilotWhereApi = (path) => axios.get(`${base()}/where`, { ...authHeaders(), params: { path } }).then(unwrap);
export const searchCopilotRefsApi = (q) => axios.get(`${base()}/refs`, { ...authHeaders(), params: { q } }).then(unwrap);
export const approveOperatorPlanApi = (planId) => axios.post(`${base()}/plans/${planId}/approve`, {}, authHeaders()).then(unwrap);
export const rejectOperatorPlanApi = (planId) => axios.post(`${base()}/plans/${planId}/reject`, {}, authHeaders()).then(unwrap);
// One plan with its step results (polled while a run is going), and undo by its coded reverse.
export const getOperatorPlanApi = (planId) => axios.get(`${base()}/plans/${planId}`, authHeaders()).then(unwrap);
export const undoOperatorPlanApi = (planId) => axios.post(`${base()}/plans/${planId}/undo`, {}, authHeaders()).then(unwrap);
export const cleanupOperatorPlanApi = (planId) => axios.post(`${base()}/plans/${planId}/cleanup`, {}, authHeaders()).then(unwrap);
// Copilot home: what needs the person and what is running for them.
export const getOperatorInboxApi = () => axios.get(`${base()}/inbox`, authHeaders()).then(unwrap);

/**
 * Send one message and read the turn's events. onEvent receives
 * {type: turn|interim|content|feed|plan|error|done, ...}.
 */
const browserTimeZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch (e) { return null; }
};

export const streamOperatorMessage = async (chatId, content, { signal, onEvent, context, refs, queuedIds, retryId, attachments, continuePlan } = {}) => {
  const response = await fetch(`${base()}/chats/${chatId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getUserToken()}` },
    // The side panel sends the page it is on and the @ references.
    // queued_ids: messages sent during the last turn that it ended before reading.
    // The browser's time zone rides along, so Copilot gives times in it.
    // attachments: ids of files uploaded for this message.
    body: JSON.stringify({ content, context: { ...(context || {}), ...(browserTimeZone() ? { tz: browserTimeZone() } : {}) }, ...(refs && refs.length ? { refs } : {}), ...(queuedIds && queuedIds.length ? { queued_ids: queuedIds } : {}), ...(retryId ? { retry_id: retryId } : {}), ...(attachments && attachments.length ? { attachments } : {}), ...(continuePlan ? { continue_plan: continuePlan } : {}) }),
    signal,
  });
  if (!response.ok) {
    let message = 'The message could not be sent.';
    try { message = (await response.json()).message || message; } catch (_) { /* not json */ }
    throw new Error(message);
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try { onEvent?.(JSON.parse(line.slice(6))); } catch (_) { /* skip malformed */ }
    }
  }
};

// Tasks: durable work beside the conversation.
export const listOperatorTasksApi = (status, template, studyId) => {
  const q = [status ? `status=${encodeURIComponent(status)}` : null, template ? `template=${encodeURIComponent(template)}` : null, studyId ? `study_id=${encodeURIComponent(studyId)}` : null].filter(Boolean).join('&');
  return axios.get(`${base()}/tasks${q ? `?${q}` : ''}`, authHeaders()).then(unwrap);
};
export const getOperatorTaskApi = (taskId) => axios.get(`${base()}/tasks/${taskId}`, authHeaders()).then(unwrap);
export const getOperatorTaskTranscriptApi = (taskId) => axios.get(`${base()}/tasks/${taskId}/transcript`, authHeaders()).then(unwrap);
export const replyOperatorTaskApi = (taskId, text, standing = false) => axios.post(`${base()}/tasks/${taskId}/reply`, { text, standing }, authHeaders()).then(unwrap);
export const confirmOperatorTaskApi = (taskId, edits = {}) => axios.post(`${base()}/tasks/${taskId}/confirm`, edits, authHeaders()).then(unwrap);
export const pauseOperatorTaskApi = (taskId) => axios.post(`${base()}/tasks/${taskId}/pause`, {}, authHeaders()).then(unwrap);
export const resumeOperatorTaskApi = (taskId) => axios.post(`${base()}/tasks/${taskId}/resume`, {}, authHeaders()).then(unwrap);
export const stopOperatorTaskApi = (taskId) => axios.post(`${base()}/tasks/${taskId}/stop`, {}, authHeaders()).then(unwrap);
export const runOperatorTaskApi = (taskId) => axios.post(`${base()}/tasks/${taskId}/run`, {}, { ...authHeaders(), timeout: 20 * 60 * 1000 }).then(unwrap);
export const approveOperatorTaskApi = (taskId) => axios.post(`${base()}/tasks/${taskId}/approve`, {}, authHeaders()).then(unwrap);
export const rejectOperatorTaskApi = (taskId) => axios.post(`${base()}/tasks/${taskId}/reject`, {}, authHeaders()).then(unwrap);
export const deleteOperatorTaskApi = (taskId) => axios.delete(`${base()}/tasks/${taskId}`, authHeaders()).then(unwrap);

// Memory: what Copilot remembers about the signed-in person.
export const listMemoryApi = () => axios.get(`${base()}/memory`, authHeaders()).then(unwrap);
export const createMemoryApi = (body) => axios.post(`${base()}/memory`, body, authHeaders()).then(unwrap);
export const editMemoryApi = (id, body) => axios.patch(`${base()}/memory/${id}`, body, authHeaders()).then(unwrap);
export const deleteMemoryApi = (id) => axios.delete(`${base()}/memory/${id}`, authHeaders()).then(unwrap);
export const acceptMemoryApi = (id) => axios.post(`${base()}/memory/${id}/accept`, {}, authHeaders()).then(unwrap);
export const dismissMemoryApi = (id) => axios.post(`${base()}/memory/${id}/dismiss`, {}, authHeaders()).then(unwrap);

// Fielding simulator (staging only).
export const getSimulationApi = (taskId) => axios.get(`${base()}/tasks/${taskId}/simulation`, authHeaders()).then(unwrap);
export const simulateApi = (taskId, body) => axios.post(`${base()}/tasks/${taskId}/simulation`, body, authHeaders()).then((r) => ({ ...(r.data?.data || {}), message: r.data?.message }));
export const clearSimulationApi = (taskId) => axios.delete(`${base()}/tasks/${taskId}/simulation`, authHeaders()).then((r) => ({ ...(r.data?.data || {}), message: r.data?.message }));
